import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config/routes';
import { useProductSearch } from 'hooks/useProductSearch';
import { useCanSeePrices } from 'hooks/useCanSeePrices';
import { formatCurrency } from 'utils/formatters';
import { getLazyImageProps, getProductDisplayName, getProductImageUrl } from 'utils/productImage';
import { findExactProductMatch } from 'utils/exactProductCode';

interface SmartSearchProps {
  fullWidth?: boolean;
  contrast?: boolean;
}

const SUGGESTION_LIMIT = 12;
const DROPDOWN_MAX_HEIGHT = 360;
const THUMB_SIZE = 56;

/** Resalta coincidencias del término de búsqueda dentro del título. */
const highlightMatch = (text: string, query: string): React.ReactNode => {
  const term = query.trim();
  if (!term) return text;

  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <Box component="strong" key={`${part}-${index}`} sx={{ fontWeight: 700 }}>
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    ),
  );
};

const SmartSearch: React.FC<SmartSearchProps> = ({ fullWidth = false, contrast = false }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const canSeePrices = useCanSeePrices();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { results, loading, totalCount } = useProductSearch(query, SUGGESTION_LIMIT);

  const showDropdown = open && query.trim().length >= 2;

  const closeDropdown = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!showDropdown) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown, closeDropdown]);

  const goToSearchPage = () => {
    if (!query.trim()) return;
    closeDropdown();
    navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(query.trim())}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const exact = findExactProductMatch(query, results);
    if (exact) {
      const slug = exact.slug?.trim() && exact.slug !== 'item' ? exact.slug : exact.sku || exact.id;
      closeDropdown();
      setQuery('');
      navigate(ROUTES.PRODUCT.replace(':slug', encodeURIComponent(slug)));
      return;
    }
    goToSearchPage();
  };

  const handleSelectProduct = () => {
    closeDropdown();
    setQuery('');
  };

  return (
    <Box
      ref={containerRef}
      component="form"
      onSubmit={handleSubmit}
      sx={{
        position: 'relative',
        flexGrow: 1,
        maxWidth: fullWidth ? { xs: '100%', md: 640 } : 480,
        width: fullWidth ? '100%' : undefined,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={t('nav.search')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        inputProps={{
          role: 'combobox',
          'aria-label': t('nav.search'),
          'aria-autocomplete': 'list',
          'aria-haspopup': 'listbox',
          'aria-expanded': showDropdown,
          'aria-controls': showDropdown ? 'smart-search-suggestions' : undefined,
        }}
        sx={
          contrast
            ? {
                bgcolor: 'common.white',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'common.white',
                },
              }
            : undefined
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton type="submit" aria-label="buscar" edge="end">
                <SearchIcon color="primary" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {showDropdown && (
        <Paper
          id="smart-search-suggestions"
          elevation={4}
          role="listbox"
          aria-label={t('nav.search')}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal,
            mt: 0.5,
            maxHeight: DROPDOWN_MAX_HEIGHT,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {loading && results.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : results.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
              Sin resultados
            </Typography>
          ) : (
            <List disablePadding>
              {results.map((product, index) => {
                const displayName = getProductDisplayName(product);
                const slug = product.slug || product.id;
                const productPath = ROUTES.PRODUCT.replace(':slug', encodeURIComponent(slug));
                const imageSrc = getProductImageUrl(product.imageUrl, product.sku || product.slug);
                const currency = product.currency || 'ARS';

                return (
                  <React.Fragment key={product.id || slug}>
                    {index > 0 && <Divider component="li" />}
                    <ListItemButton
                      component={RouterLink}
                      to={productPath}
                      role="option"
                      onClick={handleSelectProduct}
                      alignItems="flex-start"
                      sx={{
                        gap: 1.5,
                        py: 1.25,
                        px: 1.5,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box
                        component="img"
                        {...getLazyImageProps(imageSrc, displayName)}
                        sx={{
                          width: THUMB_SIZE,
                          height: THUMB_SIZE,
                          objectFit: 'contain',
                          flexShrink: 0,
                          bgcolor: 'grey.50',
                        }}
                      />
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ lineHeight: 1.35, mb: canSeePrices ? 0.5 : 0 }}
                          >
                            {highlightMatch(displayName, query)}
                          </Typography>
                        }
                        secondary={
                          canSeePrices ? (
                            <Typography
                              variant="subtitle1"
                              component="span"
                              sx={{ fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}
                            >
                              {formatCurrency(product.price, currency)}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
              {totalCount > results.length && (
                <>
                  <Divider />
                  <ListItemButton onClick={goToSearchPage} sx={{ justifyContent: 'center', py: 1 }}>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      Ver todos los resultados ({totalCount})
                    </Typography>
                  </ListItemButton>
                </>
              )}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SmartSearch;
